import { pipe, O, RA, RR } from '@packages/prelude'
import * as domain from '@packages/domain'
import * as select from '../selectors'
import { Configuration } from '~/store'

/**
 * Helper functions for components to use with configuration state
 * These are pure functions that can be imported where needed
 */

export const isOptionSelected = (
  state: Configuration.State,
  stepId: domain.StepId.Type,
  optionId: domain.OptionId.Type
): boolean => {
  const stepOptions = state.selectedOptions[String(stepId)] || []
  return stepOptions.some(option => option.id === optionId)
}

export const getStepSelectedOptions = (
  state: Configuration.State,
  stepId: domain.StepId.Type
): ReadonlyArray<domain.Option.Type> => {
  return state.selectedOptions[String(stepId)] || []
}

/**
 * Prune configuration state to remove invalid selections
 * Walks through the configuration path and only keeps valid selections
 */
export const pruneConfigurationState = (state: Configuration.State): Configuration.State => {
  const validSelections = pipe(
    state,
    select.Configuration.getFirstStepId,
    O.map(firstStepId => unfoldConfigurationPath(firstStepId, state)),
    O.getOrElse(() => RR.empty as Configuration.SelectedOptions)
  )

  return {
    ...state,
    selectedOptions: validSelections
  }
}

/**
 * Unfold configuration path recursively to build valid selection tree
 */
const unfoldConfigurationPath = (
  currentStepId: domain.StepId.Type,
  state: Configuration.State,
  acc: Configuration.SelectedOptions = RR.empty
): Configuration.SelectedOptions => {
  const stepKey = String(currentStepId)
  const currentStepOptions = state.selectedOptions[stepKey]

  return pipe(
    currentStepOptions,
    O.fromNullable,
    O.filter(RA.isNonEmpty),
    O.map(options => {
      const updatedAcc = { ...acc, [stepKey]: options }

      // Only single-select steps support references
      const nextStepId = pipe(
        options,
        RA.head,
        O.chain(option =>
          pipe(
            option.referencedStepIds,
            RA.head,
            O.alt(() => select.Configuration.getNextSequentialStep(currentStepId, state))
          )
        )
      )

      return pipe(
        nextStepId,
        O.map(stepId => unfoldConfigurationPath(stepId, state, updatedAcc)),
        O.getOrElse(() => updatedAcc)
      )
    }),
    O.getOrElse(() => acc)
  )
}
